import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { Handle, Position } from "@xyflow/react";
import { NodeContent } from "../content";
import MultiConnections from "../connections/multi-connections";
import Connection from "../connections";
import ConnectionsWithExpiration from "../connections/with-expiration";
import MultiInputs from "../connections/multi-inputs";
import MultiMergeInputs from "../connections/multi-merge-inputs";

interface WorkflowNodeProps {
  id: string;
  data: NodeData;
  selected?: boolean;
  dragging?: boolean;
}

const ExecutionNode = ({ data, id, dragging }: WorkflowNodeProps) => {
  const {
    nodeConnecting,
    selectedNodes,
    deletingNode,
    setSelectedNodeId,
    getNodeById,
    setLastClickPosition,
    executionData,
  } = useWorkflowStore();
  const { selectedNodeId, setConnectionToSplit, setNodeCreatorOpen } =
    useUIStore();
  const isConnecting = nodeConnecting === id;
  const isSelected = selectedNodeId === id || selectedNodes.includes(id);
  const isDeleting = deletingNode === id;

  const handleNodeClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedNodeId(id);
  };

  const handleAddNode = (event: React.MouseEvent, outputId: string) => {
    event.stopPropagation();

    // Get current node's position
    const currentNode = getNodeById(id);
    const currentPosition = currentNode?.position || { x: 0, y: 0 };

    // Set position for new node (offset to the right of the current node)
    setLastClickPosition({
      x: currentPosition.x + 200, // 200px to the right of current node
      y: currentPosition.y, // Same vertical position
    });

    // Store the source handle info for auto-connection
    setConnectionToSplit({
      edgeId: "", // Not splitting, creating new connection
      sourceNodeId: id,
      targetNodeId: "", // Will be the new node
      sourceHandle: outputId,
      targetHandle: "main", // Default target handle
    });

    // Open node creator
    setNodeCreatorOpen(true);
  };

  const getNodeExecutionLabel = () => {
    if (executionData) {
      const nodeExecutions = executionData.node_executions
        .filter(({ node_id }) => {
          return node_id === id;
        })
        .sort((a, b) => {
          // if ended_at is null, put it last
          if (a.ended_at === null) return 1;
          if (b.ended_at === null) return -1;
          // sort by ended_at descending
          return (
            new Date(b.ended_at).getTime() - new Date(a.ended_at).getTime()
          );
        });
      if (nodeExecutions && nodeExecutions.length > 0) {
        const nodeExecution = nodeExecutions[0];
        return nodeExecution.status;
      }
    }
    return "";
  };

  const getNodeStatusColor = () => {
    if (executionData) {
      const nodeExecutions = executionData.node_executions
        .filter(({ node_id }) => {
          return node_id === id;
        })
        .sort((a, b) => {
          // if ended_at is null, put it last
          if (a.ended_at === null) return 1;
          if (b.ended_at === null) return -1;
          // sort by ended_at descending
          return (
            new Date(b.ended_at).getTime() - new Date(a.ended_at).getTime()
          );
        });
      const nodeExecution = nodeExecutions[0];
      if (nodeExecution) {
        switch (nodeExecution.status) {
          case "COMPLETED":
            return "#22c55e"; // green-500
          case "FAILED":
            return "#dc2626"; // red-500
          case "RUNNING":
            return "#3b82f6"; // amber-500
          case "PENDING":
            return "#f59e0b"; // indigo-500
          case "CANCELLED":
            return "#f97316"; // gray-400
          case "EXPIRED":
            return "#7c2d12"; // gray-500
          case "UNPROCESSED":
            return "#9ca3af"; // gray-300
          default:
            return "#6b7280"; // gray-500
        }
      }
    }

    // switch (data.execution.status) {
    //   case "success":
    //     return "#10b981"; // green-500
    //   case "error":
    //     return "#ef4444"; // red-500
    //   case "running":
    //     return "#f59e0b"; // amber-500
    //   case "waiting":
    //     return "#6366f1"; // indigo-500
    //   default:
    //     return "#6b7280"; // gray-500
    // }
  };

  const isNodeInitializer = data.type === "initializer-node";

  const mergeInputs = (() => {
    if (data.type !== NodeTypes.MERGE) return null;

    return (
      data.inputs
        ?.filter((input) => input.type === "MERGE_ENTRY")
        .map((entry, index) => {
          const entryValue =
            typeof entry.value === "string"
              ? entry.value
                ? JSON.parse(entry.value)
                : {}
              : entry.value || {};

          return {
            id: `merge-input-${index + 1}`,
            name: entryValue?.name || `Entrada ${index + 1}`,
            node_id: entryValue?.node_id || "",
            hasConnection:
              Array.isArray(entry.source_connections) &&
              entry.source_connections.length > 0,
          };
        }) || []
    );
  })();

  const getNodeSize = () => {
    let height = 120; // Default height for most nodes

    if (data.type === NodeTypes.WAIT) {
      const threadInputs =
        data.inputs?.filter((input) => input.type === "THREAD") || [];
      const inputsCount = threadInputs.length;
      if (inputsCount <= 3) {
        height = 120;
      } else if (inputsCount > 3) {
        height = 120 + (inputsCount - 2) * 40;
      }
    } else if (data.type === NodeTypes.MERGE) {
      const inputsCount = mergeInputs?.length || 0;
      if (inputsCount <= 3) {
        height = 120;
      } else if (inputsCount > 3) {
        height = 120 + (inputsCount - 2) * 40;
      }
    } else if (data.type === NodeTypes.SWITCH) {
      const connectionsLength = Object.keys(data.connections || {}).length;
      if (data.connections && connectionsLength <= 3) {
        height = 120; // Default height for switch with 2 or fewer outputs
      } else if (connectionsLength > 3) {
        height = 120 + (connectionsLength - 2) * 40; // 10px for each additional output
      }
    } else if (
      data.type === NodeTypes.IF ||
      data.type === NodeTypes.MULTITHREAD ||
      data.type === NodeTypes.INTEROPERABILITY ||
      (data.connections && Object.keys(data.connections).length > 1)
    ) {
      const connectionsLength = Object.keys(data.connections || {}).length;
      if (connectionsLength <= 3) {
        height = 120;
      } else if (connectionsLength > 3) {
        height = 120 + (connectionsLength - 2) * 40;
      }
    }
    return {
      width: 120,
      height,
    };
  };

  const nodeStatusColor = getNodeStatusColor();
  return (
    <div
      className={`
        workflow-node
        ${isSelected ? "selected" : ""}
        ${dragging ? "dragging" : ""}
        ${isDeleting ? "deleting" : ""}
        `}
      // ${data.disabled ? "disabled" : ""}
      // ${data.execution.status || "new"}
      // ${data.render.type}
      style={{
        width: getNodeSize().width,
        height: getNodeSize().height,
        borderColor: nodeStatusColor || "var(--border-neutral)",
      }}
      onClick={handleNodeClick}
    >
      {/* Input handles */}
      {!isNodeInitializer &&
        data.type !== NodeTypes.WAIT &&
        data.type !== NodeTypes.MERGE && (
          <div
            style={{
              position: "absolute",
              top: "51%",
              left: -20,
              transform: "translateY(-50%)",
              width: "20px",
              height: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "8px",
            }}
          >
            <Handle
              type="target"
              id="target"
              position={Position.Left}
              className="!rounded-none !border-none !bg-transparent z-40 !left-0 no-drag"
              style={{
                position: "relative",
                top: "50%",
                ...(data.edge
                  ? {
                      transform: "translateY(-50%)",
                      width: "20px",
                      pointerEvents: "none",
                    }
                  : {
                      transform: "translateY(-50%) translateX(0%)",
                    }),
              }}
            >
              <div
                className="w-[3px] h-3.5 bg-on-disabled rounded-l-full absolute right-px pointer-events-none"
                style={{
                  backgroundColor: nodeStatusColor || "var(--on-disabled)",
                }}
              ></div>
              {data.edge && (
                <div
                  className="w-[20px] h-px border border-neutral absolute top-1.5 -z-10 -left-0.5 pointer-events-none"
                  style={{
                    borderColor: nodeStatusColor || "var(--border-neutral)",
                  }}
                ></div>
              )}
            </Handle>
          </div>
        )}
      {data.type === NodeTypes.WAIT && data.inputs && (
        <MultiInputs
          node_id={id}
          inputs={
            // Convertir inputs THREAD al formato simplificado
            data.inputs
              .filter((input) => input.type === "THREAD")
              .map((input, index) => ({
                id: input.id,
                name: input.name || `Entrada ${index + 1}`,
                hasConnection:
                  Array.isArray(input.source_connections) &&
                  input.source_connections.length > 0,
              }))
          }
          isSelected={isSelected}
          isDeleting={isDeleting}
          isExecutionView
        />
      )}
      {data.type === NodeTypes.MERGE && mergeInputs && (
        <MultiMergeInputs
          node_id={id}
          inputs={mergeInputs}
          isSelected={isSelected}
          isDeleting={isDeleting}
          isExecutionView
        />
      )}
      <NodeContent
        displayName={data.displayName}
        id={id}
        type={data.type}
        isExecutionView
        executionStatus={getNodeExecutionLabel()}
      />
      {data.type !== NodeTypes.END && (
        <>
          {data.type === NodeTypes.IF ||
          data.type === NodeTypes.SWITCH ||
          data.type === NodeTypes.MULTITHREAD ||
          data.type === NodeTypes.INTEROPERABILITY ||
          (data.connections && Object.keys(data.connections).length > 1) ? (
            <MultiConnections
              node_id={id}
              connections={data.connections || {}}
              isConnecting={isConnecting}
              onAdd={handleAddNode}
              isSelected={isSelected}
              isDeleting={isDeleting}
              isExecutionView
            />
          ) : data.connections?.["expired"] ? (
            <ConnectionsWithExpiration
              id={id}
              isConnecting={isConnecting}
              onAdd={handleAddNode}
              connections={data.connections}
              isSelected={isSelected}
              isDeleting={isDeleting}
              isExecutionView
            />
          ) : (
            <Connection
              id={id}
              isConnecting={isConnecting}
              onAdd={handleAddNode}
              isSelected={isSelected}
              isDeleting={isDeleting}
              isExecutionView
            />
          )}
        </>
      )}
    </div>
  );
};

export default ExecutionNode;
